// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {fetchSlowmodeState} from '@app/features/channel/commands/ChannelCommands';
import {ReplyBar} from '@app/features/channel/components/ChannelReplyBar';
import {SlowmodeIndicator} from '@app/features/channel/components/SlowmodeIndicator';
import type {Channel} from '@app/features/channel/models/Channel';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import type {Gif} from '@app/features/expressions/commands/GifCommands';
import {GifPickerSelectModal} from '@app/features/expressions/components/modals/GifPickerSelectModal';
import {ExpressionPickerPopout} from '@app/features/expressions/components/popouts/ExpressionPickerPopout';
import * as GifSlugUtils from '@app/features/expressions/utils/GifSlugUtils';
import {feedEmojiText, insertAtSelection} from '@app/features/feed/utils/FeedExpressionUtils';
import {CANNOT_SEND_MESSAGES_IN_CHANNEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {useMessageSubmission} from '@app/features/messaging/hooks/useMessageSubmission';
import MessageReply from '@app/features/messaging/state/MessageReply';
import Permission from '@app/features/permissions/state/Permission';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import {useSlowmode} from '@app/features/slowmode/hooks/useSlowmode';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import Users from '@app/features/user/state/Users';
import styles from '@app/features/voice_scene/components/VoiceSceneChatBox.module.css';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {GifIcon, SmileyIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const SAY_SOMETHING_DESCRIPTOR = msg({
	message: 'Say something',
	comment:
		"Voice call 3D room: placeholder of the small message box in the room's toolbar. The message goes to this voice channel's chat and shows over your character as a speech bubble.",
});
const OPEN_EMOJI_PICKER_DESCRIPTOR = msg({
	message: 'Open emoji picker',
	comment: 'Voice call 3D room: tooltip and accessible label of the smiley button in the room message box.',
});
const CHOOSE_A_GIF_DESCRIPTOR = msg({
	message: 'Choose a GIF',
	comment:
		'Voice call 3D room: tooltip and accessible label of the GIF button in the room message box, and title of the GIF picker it opens. The picked GIF is sent at once.',
});

// A short line into this voice channel's own chat, through the composer's send path (no draft, no staged uploads); the
// room shows it as a bubble over the character. Keys typed here stay out of the room's bare-key shortcuts: the
// keybind manager skips an editable that is not the channel composer, and the room's Esc skips text fields. While
// Fluxer's slowmode holds, nothing is sent and the text stays, with the time left beside it. The chat's Reply answers
// through this box, the room having no channel composer: it takes Fluxer's focus request, sends the reply, and its Esc
// cancels the reply first.
export const VoiceSceneChatBox = observer(function VoiceSceneChatBox({channel}: {channel: Channel}) {
	const {i18n} = useLingui();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [value, setValue] = useState('');
	const canSend = Permission.can(Permissions.SEND_MESSAGES, channel);
	const replyingMessage = MessageReply.getReplyingMessage(channel.id);
	const {sendOptimisticMessage} = useMessageSubmission({
		accountKey: Users.viewAccountKey,
		channel,
		referencedMessage: MessageReply.getReferencedMessage(channel.id),
		replyingMessage,
	});
	const {isSlowmodeActive, slowmodeRemaining, isSlowmodeImmune} = useSlowmode(channel);
	const hasSlowmode = Boolean(channel.guildId) && channel.rateLimitPerUser > 0;
	useEffect(() => {
		if (hasSlowmode) void fetchSlowmodeState(channel.id);
	}, [channel.id, hasSlowmode]);
	useEffect(
		() =>
			ComponentBus.subscribe('FOCUS_TEXTAREA', (payload?: unknown) => {
				if ((payload as {channelId?: string} | undefined)?.channelId === channel.id) textareaRef.current?.focus();
			}),
		[channel.id],
	);
	const send = (content: string): boolean => {
		if (isSlowmodeActive) return false;
		sendOptimisticMessage({content}, {hasAttachments: false});
		return true;
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === 'Escape') {
			if (replyingMessage) MessageCommands.stopReply(channel.id);
			else event.currentTarget.blur();
			return;
		}
		if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
		event.preventDefault();
		const content = value.trim();
		if (content && send(content)) setValue('');
	};
	// The open picker keeps the render it was opened with, so the text is read from the box itself.
	const selectEmoji = (emoji: FlatEmoji) => {
		const textarea = textareaRef.current;
		const current = textarea?.value ?? value;
		const start = textarea?.selectionStart ?? current.length;
		const next = insertAtSelection(current, start, textarea?.selectionEnd ?? start, feedEmojiText(emoji));
		if (next.value.length > ROOM_LIMITS.bubbleTextMax) return;
		setValue(next.value);
		requestAnimationFrame(() => {
			textarea?.focus();
			textarea?.setSelectionRange(next.caret, next.caret);
		});
	};
	const gifLabel = i18n._(CHOOSE_A_GIF_DESCRIPTOR);
	const openGifPicker = () =>
		ModalCommands.push(
			ModalCommands.modal(() => (
				<GifPickerSelectModal
					title={gifLabel}
					onSelect={(gif: Gif) => send(GifSlugUtils.resolveShareUrl(gif.provider, {url: gif.url, slug: gif.slug}))}
					data-flx="voice-scene.voice-scene-chat-box.gif-picker-select-modal"
				/>
			)),
		);
	const emojiLabel = i18n._(OPEN_EMOJI_PICKER_DESCRIPTOR);

	return (
		<div className={styles.box} data-flx="voice-scene.voice-scene-chat-box.box">
			<textarea
				ref={textareaRef}
				className={styles.input}
				rows={1}
				value={value}
				maxLength={ROOM_LIMITS.bubbleTextMax}
				disabled={!canSend}
				placeholder={i18n._(canSend ? SAY_SOMETHING_DESCRIPTOR : CANNOT_SEND_MESSAGES_IN_CHANNEL_DESCRIPTOR)}
				aria-label={i18n._(SAY_SOMETHING_DESCRIPTOR)}
				onChange={(event) => setValue(event.target.value)}
				onFocus={(event) => {
					const textarea = event.currentTarget;
					const canvas = textarea
						.closest('[data-flx="voice-scene.voice-scene-view.scene"]')
						?.querySelector('[data-flx="voice-scene.voice-scene-view.stage"] canvas');
					if (canvas && textarea.ownerDocument.pointerLockElement === canvas) textarea.ownerDocument.exitPointerLock();
				}}
				onKeyDown={onKeyDown}
				data-flx="voice-scene.voice-scene-chat-box.input"
			/>
			{isSlowmodeActive ? (
				<div className={styles.slowmode} data-flx="voice-scene.voice-scene-chat-box.slowmode">
					<SlowmodeIndicator
						slowmodeRemaining={slowmodeRemaining}
						slowmodeDuration={channel.rateLimitPerUser * 1000}
						isImmune={isSlowmodeImmune}
						data-flx="voice-scene.voice-scene-chat-box.slowmode-indicator"
					/>
				</div>
			) : null}
			<Popout
				position="top-end"
				animationType="none"
				tooltip={emojiLabel}
				returnFocusRef={textareaRef}
				render={({onClose}) => (
					<ExpressionPickerPopout
						channelId={channel.id}
						onEmojiSelect={selectEmoji}
						onClose={onClose}
						visibleTabs={['emojis']}
						data-flx="voice-scene.voice-scene-chat-box.expression-picker-popout"
					/>
				)}
				data-flx="voice-scene.voice-scene-chat-box.popout"
			>
				<FocusRing offset={-2} data-flx="voice-scene.voice-scene-chat-box.emoji-focus-ring">
					<button
						type="button"
						className={styles.button}
						disabled={!canSend}
						aria-label={emojiLabel}
						aria-haspopup="dialog"
						data-flx="voice-scene.voice-scene-chat-box.emoji-button"
					>
						<SmileyIcon
							weight="bold"
							className={styles.icon}
							aria-hidden
							data-flx="voice-scene.voice-scene-chat-box.smiley-icon"
						/>
					</button>
				</FocusRing>
			</Popout>
			{RuntimeConfig.gifEnabled ? (
				<Tooltip text={gifLabel} data-flx="voice-scene.voice-scene-chat-box.gif-tooltip">
					<FocusRing offset={-2} data-flx="voice-scene.voice-scene-chat-box.gif-focus-ring">
						<button
							type="button"
							className={styles.button}
							disabled={!canSend || isSlowmodeActive}
							onClick={openGifPicker}
							aria-label={gifLabel}
							aria-haspopup="dialog"
							data-flx="voice-scene.voice-scene-chat-box.gif-button.open-gif-picker"
						>
							<GifIcon
								weight="bold"
								className={styles.icon}
								aria-hidden
								data-flx="voice-scene.voice-scene-chat-box.gif-icon"
							/>
						</button>
					</FocusRing>
				</Tooltip>
			) : null}
		</div>
	);
});

// Fluxer's reply bar over the room's message box, on the room's dark material on every theme.
export const VoiceSceneChatReply = observer(function VoiceSceneChatReply({
	channel,
	className,
}: {
	channel: Channel;
	className?: string;
}) {
	const replying = MessageReply.getReplyingMessage(channel.id);
	const message = MessageReply.getReferencedMessage(channel.id);
	if (!replying || !message) return null;
	return (
		<div className={clsx(className, 'theme-coal')} data-flx="voice-scene.voice-scene-chat-box.reply">
			<ReplyBar
				replyingMessageObject={message}
				shouldReplyMention={replying.mentioning}
				setShouldReplyMention={(mentioning) => MessageCommands.setReplyMentioning(channel.id, mentioning)}
				channel={channel}
				data-flx="voice-scene.voice-scene-chat-box.reply-bar"
			/>
		</div>
	);
});
